import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { complianceCheck } from '../../utils/advisorNetworkCompliance';
import { getStoredAdvisorId } from '../../utils/advisorNetworkConstants';

const MAX_CHARS = 500;
const DEBOUNCE_MS = 800;

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

const AdvisorPostCompose = ({ user }) => {
  const navigate = useNavigate();
  const advisorId = getStoredAdvisorId(user);

  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState(null);

  const debouncedContent = useDebouncedValue(content, DEBOUNCE_MS);
  const livePreview = useMemo(() => complianceCheck(content), [content]);
  const debouncedPreview = useMemo(() => complianceCheck(debouncedContent), [debouncedContent]);
  const charCount = content.length;

  const counterClass =
    charCount >= 490 ? 'text-danger' : charCount >= 450 ? 'text-warning' : 'text-ink-muted';

  const canSubmit = content.trim().length > 0 && charCount <= MAX_CHARS && !submitting;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);
    setSuccessMessage('');

    try {
      await axios.post('/api/advisor-network/posts', { content: content.trim() });
      setContent('');
      setSuccessMessage('Posted successfully');
      window.setTimeout(() => {
        if (advisorId) {
          navigate(`/advisor-network/${advisorId}`);
        } else {
          navigate('/advisor-network');
        }
      }, 800);
    } catch (err) {
      console.error('Post failed:', err);
      setError(err.response?.data?.detail || 'Failed to publish post.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone">
      <div className="max-w-2xl mx-auto px-6 md:px-8 py-10 md:py-14">
        <Link
          to={advisorId ? `/advisor-network/${advisorId}` : '/advisor-network'}
          className="inline-flex items-center text-body text-ink-muted hover:text-ink mb-8 transition-colors duration-300"
        >
          ← Back
        </Link>

        <h1 className="font-display text-h1 text-ink mb-8">New Post</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="relative">
            <textarea
              value={content}
              onChange={(event) => setContent(event.target.value.slice(0, MAX_CHARS))}
              placeholder="Share an insight, answer a question, or post market commentary..."
              rows={8}
              className="input-editorial resize-y min-h-[10rem] rounded-md"
              aria-label="Post content"
            />
            <span className={`absolute bottom-3 right-3 text-label ${counterClass}`}>
              {charCount}/{MAX_CHARS}
            </span>
          </div>

          {(debouncedPreview.flagged_words.length > 0 || debouncedPreview.disclaimer_added) &&
            debouncedContent.trim() && (
            <div className="space-y-3">
              {debouncedPreview.flagged_words.length > 0 && (
                <div className="px-4 py-3 border border-warning/40 bg-warning/10 rounded-md">
                  <p className="text-body text-ink">
                    Flagged language detected: {debouncedPreview.flagged_words.join(', ')}. Your post
                    will still publish but review is recommended.
                  </p>
                </div>
              )}
              {debouncedPreview.disclaimer_added && (
                <div className="px-4 py-3 border border-pine/30 bg-pine-tint rounded-md">
                  <p className="text-body text-pine">
                    A regulatory disclaimer will be appended to this post.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="border border-hairline rounded-lg bg-bone-raised p-4">
            <p className="text-label text-ink-muted mb-2">Preview</p>
            {content.trim() ? (
              <p className="text-body text-ink whitespace-pre-wrap leading-relaxed">
                {livePreview.clean_content}
              </p>
            ) : (
              <p className="text-body text-ink-muted italic">Start typing to see preview…</p>
            )}
          </div>

          {error && <p className="text-body text-danger">{error}</p>}
          {successMessage && <p className="text-body text-pine">{successMessage}</p>}

          <button type="submit" disabled={!canSubmit} className="btn-primary">
            {submitting ? 'Posting…' : 'Post'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdvisorPostCompose;
